import clsx from "clsx";
import _ from "lodash";
import moment from "moment";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";

import { ContinuousUpdateStatus, HISTORY_ALERT_COLOR, SafetyInstructionNumbers } from "../../constants";
import { useBulkEditorDialog } from "../../hooks/useBulkEditorDialog";
import { useContinuousUpdate } from "../../hooks/useContinuousUpdate";
import { useEditorDialog } from "../../hooks/useEditorDialog";
import { UseSearchInputType } from "../../hooks/useSearchInput";
import { BulkUpdateOption, ScheduleConfig, ScheduleSchedule, ScheduleItem, ScheduleHistory } from "../../types";
import { GridCell } from "../GridCell";
import { ScheduleEditorDialog } from "../ScheduleEditorDialog";

import actions from "@/actions";
import { ConfigurablePlanListCell } from "@/components/list/Schedule/components/PlanList/ConfigurablePlanListCell";
import { ConfigurablePlanListHeaderColumn } from "@/components/list/Schedule/components/PlanList/ConfigurablePlanListHeaderColumn";
import { ScheduleHistoryDialog } from "@/components/list/Schedule/components/ScheduleHistoryDialog";
import { useTextEditor } from "@/components/list/Schedule/hooks/useTextEditor";
import { getConstructionId, isLinuxOS, isWinOS, omitString } from "@/lib/common";
import { RootState } from "@/reducers/types";

type PlanListContentsProps = {
  bulkUpdateOptions: BulkUpdateOption[];
  condition: UseSearchInputType;
  continuousUpdateStatus: number;
  currentPageOfScheduleList: number;
  data: ScheduleSchedule;
  fetching: boolean;
  scheduleDate: Date;
  scheduleConfig: ScheduleConfig;
  total: number;
  setTooltipText: (text: string) => void;
  onSearch: (option?: { page?: number; isPartialUpdate?: boolean; preventRefresh?: boolean }) => void;
};

export const PlanListContents: FC<PlanListContentsProps> = ({
  bulkUpdateOptions,
  condition,
  continuousUpdateStatus,
  currentPageOfScheduleList,
  data,
  fetching,
  scheduleDate,
  scheduleConfig,
  setTooltipText,
  onSearch,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const constructionId = getConstructionId();

  // 予定表項目情報
  const scheduleFields = useSelector((state: RootState) => state.schedule.fields);

  // scroll-bar表示制御
  const [isRightListOverflow, setIsRightListOverflow] = useState(true);

  useEffect(() => {
    const updateOverflowStatus = (): void => {
      // table wrapperコンテンツ幅
      const wrapperWidth = planListRef.current?.offsetWidth;
      // overflow部分を含めたコンテンツ幅
      const contentsWidth = planListLeftRef.current?.offsetWidth + tableRightHeaderRef.current?.scrollWidth;

      setIsRightListOverflow(wrapperWidth < contentsWidth);
    };

    window.addEventListener("resize", updateOverflowStatus);
    updateOverflowStatus();

    return () => window.removeEventListener("resize", updateOverflowStatus);
  }, []);

  const continuousUpdate = useContinuousUpdate();
  const editorDialog = useEditorDialog({
    bulkUpdateOptions,
    currentPage: currentPageOfScheduleList,
    onSearch: (page, isPartialUpdate) => onSearch({ page, isPartialUpdate, preventRefresh: true }),
  });
  const textEditor = useTextEditor({
    onSearch: (isPartialUpdate) => onSearch({ page: currentPageOfScheduleList, isPartialUpdate, preventRefresh: true }),
  });
  const bulkEditorDialog = useBulkEditorDialog({
    scheduleDate,
    bulkUpdateOptions,
    onSearch: () => onSearch({ page: 1, preventRefresh: true }),
    searchFilter: condition.values(),
  });

  const planListRef = useRef<HTMLDivElement>(); // リスト全体
  const tableRightHeaderRef = useRef<HTMLDivElement>();
  const tableRightBodyRef = useRef<HTMLDivElement>();
  const planListLeftRef = useRef<HTMLDivElement>(); // 固定幅の左側のリスト
  const tableLeftBodyRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (tableRightBodyRef.current?.scrollTop) {
      tableRightBodyRef.current.scrollTop = 0;
    }
  }, [currentPageOfScheduleList]);

  const { loadingItemId, loadingTarget, isLoadingSchedules } = useSelector((state: RootState) => state.schedule);

  const [editableSekou, editableSeiyu] = useMemo((): [boolean, boolean] => {
    const now = moment();

    const calcEndDate = (endDays: string, endTime: string) => {
      const date = moment(scheduleDate);
      const days = Number(endDays);
      const [hour, minute] = endTime.split(":").map((v) => Number(v));

      return (days < 0 ? date.subtract(Math.abs(days), "d") : date.add(days, "d")).set({
        hour: hour,
        minute: minute,
        second: 0,
      });
    };

    const sekou = scheduleConfig.field_options.find((v) => v.target === "sekou");
    const sekouEditEnd = sekou ? calcEndDate(sekou.edit_end_days, sekou.edit_end_time) : now;

    const seiyu = scheduleConfig.field_options.find((v) => v.target === "seiyu");
    const seiyuEditEnd = sekou ? calcEndDate(seiyu.edit_end_days, seiyu.edit_end_time) : now;

    return [now.isBefore(sekouEditEnd), now.isBefore(seiyuEditEnd)];
  }, [scheduleConfig, scheduleDate, continuousUpdateStatus]);

  // Scroll synchronization
  const syncScrollOn = useRef<"left" | "header" | "right" | null>(null);
  const clearSyncScrollControl = useMemo(
    () =>
      _.debounce(() => {
        // scrollTop/Leftの直接変更でonScrollイベントが呼ばれて相互作用しないように制御
        syncScrollOn.current = null;
      }, 100),
    []
  );

  const syncScrollByLeft = (e: React.UIEvent<HTMLDivElement>) => {
    if (syncScrollOn.current && syncScrollOn.current !== "left") return;
    syncScrollOn.current = "left";
    if (tableRightBodyRef.current) {
      tableRightBodyRef.current.scrollTop = e.currentTarget.scrollTop;
    }
    clearSyncScrollControl();
  };

  const syncScrollByHeader = (e: React.UIEvent<HTMLDivElement>) => {
    if (syncScrollOn.current && syncScrollOn.current !== "header") return;
    syncScrollOn.current = "header";
    if (tableRightBodyRef.current) {
      tableRightBodyRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
    clearSyncScrollControl();
  };

  const syncScrollByRight = (e: React.UIEvent<HTMLDivElement>) => {
    if (syncScrollOn.current && syncScrollOn.current !== "right") return;
    syncScrollOn.current = "right";
    if (tableLeftBodyRef.current) {
      tableLeftBodyRef.current.scrollTop = e.currentTarget.scrollTop;
    }
    if (tableRightHeaderRef.current) {
      tableRightHeaderRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
    clearSyncScrollControl();
  };

  const setTooltip = (text) => {
    setTooltipText(text);
    ReactTooltip.rebuild();
  };

  const bulkEditSelectItem = (targetName: string, target: string, optionKey?: string) => {
    bulkEditorDialog.openSelect({ targetName, target, optionKey });
  };

  const bulkEditTextItem = (targetName: string, target: string, option?: { multiline?: boolean }) => {
    bulkEditorDialog.openText({ targetName, target, multiline: option?.multiline });
  };

  const handleClickItemName = (item: ScheduleItem) => {
    const query = new URLSearchParams();
    query.set("category_id", item.category_id.toString());
    query.set("primary_charge_id", item.primary_charge_id.toString());
    query.set("item_id", item.item_id.toString());
    window.open(`/#/${constructionId}/matrix?${query.toString()}`, "_blank", "noreferrer");
  };

  const [historyData, setHistoryData] = useState<{ title: string; target: string; list: ScheduleHistory[] }>(null);
  const handleClickHistory = (item: ScheduleItem, targetColumn: string, targetName: string) => {
    dispatch(
      actions.schedule.getScheduleHistories(
        { schedule_id: item.schedule_task_id, target_column: targetColumn },
        (list: ScheduleHistory[]) => {
          setHistoryData({
            title: item.item_name,
            target: targetName,
            list:
              targetColumn === "field_t9"
                ? list.map((item) => ({ ...item, content_after: Number(item.content_after).toString() }))
                : targetColumn === "field_t8"
                ? list.map((item) => ({
                    ...item,
                    content_after: item.content_after
                      ?.split(",")
                      .map((v) => SafetyInstructionNumbers[v])
                      .join(""),
                  }))
                : list,
          });
        }
      )
    );
  };

  return (
    <>
      <div
        key={`plan-list-contents-${isLoadingSchedules}`} // スクロールが初期位置に戻るように再描画する
        className={clsx("plan-list-container", fetching ? "loading loading--list" : "")}
        ref={planListRef}
      >
        <div className="plan-list w-[430px]" ref={planListLeftRef}>
          <div className="header-row">
            <div data-test-id="schedule-header-device" className="col1">
              {t("device")}
            </div>
            <div data-test-id="schedule-header-item" className="col2">
              {t("title")}
            </div>
            <div data-test-id="schedule-header-process" className="col3">
              {t("content")}
            </div>
            <div data-test-id="schedule-header-manager" className="col4">
              {t("sankyu")}
              <br />
              {t("manager_title")}
            </div>
          </div>
          <div className="body-area fixed-area" onScroll={syncScrollByLeft} ref={tableLeftBodyRef}>
            {data?.list.map((item: ScheduleItem, index) => {
              const { device_name, item_name, process_name } = item;

              return (
                <div key={`plan-list-left-row-${index}`} className="body-row" style={{ backgroundColor: "#eff0f3" }}>
                  <GridCell
                    className="col1"
                    align="left"
                    label={device_name}
                    setTooltip={setTooltip}
                    data-test-id={`schedule-${index + 1}-device`}
                  >
                    {device_name}
                  </GridCell>
                  <GridCell
                    className="col2 break-word"
                    align="left"
                    label={item_name}
                    overflowLength={22}
                    setTooltip={setTooltip}
                    historyAlertColor={item.history_alert?.insert?.includes("record") && HISTORY_ALERT_COLOR.INSERT}
                    onClickHistory={() => handleClickHistory(item, "record", t("title"))}
                    data-test-id={`schedule-${index + 1}-item`}
                  >
                    <a onClick={() => handleClickItemName(item)}>{omitString(item_name, 22)}</a>
                  </GridCell>
                  <GridCell
                    className="col3 break-word"
                    align="left"
                    label={process_name}
                    overflowLength={37}
                    setTooltip={setTooltip}
                    data-test-id={`schedule-${index + 1}-process`}
                  >
                    {omitString(process_name, 37)}
                  </GridCell>
                  <GridCell className="col4" data-test-id={`schedule-${index + 1}-manager`}>
                    {item.manager_company_name}
                    {item.manager_company_name && item.manager_user_name && <br />}
                    {item.manager_user_name}
                  </GridCell>
                </div>
              );
            })}
            <div className="scroll-margin" />
          </div>
        </div>
        <div className="plan-list plan-list-right">
          <div className="header-row fixed-row" ref={tableRightHeaderRef} onScroll={syncScrollByHeader}>
            {scheduleFields.map((field) => (
              <ConfigurablePlanListHeaderColumn
                key={`schedule-header-${field.field_key}`}
                field={field}
                data={data}
                editableSekou={editableSekou}
                editableSeiyu={editableSeiyu}
                bulkEditTextItem={bulkEditTextItem}
                bulkEditSelectItem={bulkEditSelectItem}
                lastColumn={scheduleFields[scheduleFields.length - 1]?.field_key === field.field_key}
              />
            ))}
            <div className="scroll-margin" />
          </div>
          <div className="scroll-header" />
          <div className="body-area" onScroll={syncScrollByRight} ref={tableRightBodyRef}>
            {data?.list.map((item: ScheduleItem, index) => {
              const isContinuousUpdateMode = continuousUpdateStatus !== ContinuousUpdateStatus.OFF;
              const canContinuousUpdateSekou = [ContinuousUpdateStatus.CANCEL, ContinuousUpdateStatus.DO].includes(
                continuousUpdateStatus
              );
              const canContinuousUpdateSeiyu = isContinuousUpdateMode;

              const editSelectItem = (targetName: string, target: string, value?: string, optionKey?: string) => {
                if (isContinuousUpdateMode) {
                  continuousUpdate.update(item, target, continuousUpdateStatus);
                } else {
                  editorDialog.openSelect({
                    item,
                    targetName,
                    target,
                    value: value ?? item[target],
                    optionKey,
                  });
                }
              };

              return (
                <div
                  key={`plan-list-right-row-${item.schedule_task_id}-${item.timestamp?.update_date}`}
                  className="body-row"
                  style={{ backgroundColor: "#eff0f3" }}
                >
                  {scheduleFields.map((field) => (
                    <ConfigurablePlanListCell
                      key={`schedule-${index + 1}-${field.field_key}`}
                      field={field}
                      index={index}
                      item={item}
                      editableSekou={editableSekou}
                      editableSeiyu={editableSeiyu}
                      canContinuousUpdateSekou={canContinuousUpdateSekou}
                      canContinuousUpdateSeiyu={canContinuousUpdateSeiyu}
                      isContinuousUpdateMode={isContinuousUpdateMode}
                      continuousUpdateStatus={continuousUpdateStatus}
                      loadingItemId={loadingItemId}
                      loadingTarget={loadingTarget}
                      setTooltip={setTooltip}
                      editSelectItem={editSelectItem}
                      textEditor={{
                        id: textEditor.id,
                        target: textEditor.target,
                        enter: textEditor.enter,
                        submit: textEditor.submit,
                      }}
                      onClickHistory={handleClickHistory}
                    />
                  ))}
                </div>
              );
            })}
            {/* Windowsの場合、overflowしない場合にscroll-barの高さ補完用のエレメントを表示 */}
            {(!(isWinOS || isLinuxOS) || !isRightListOverflow) && <div className="scroll-margin" />}
          </div>
        </div>
      </div>

      {editorDialog.inputType && (
        <ScheduleEditorDialog
          processName={editorDialog.item?.process_name}
          targetName={editorDialog.targetName}
          target={editorDialog.target}
          type={editorDialog.inputType}
          value={editorDialog.value}
          onClose={editorDialog.close}
          onChange={(v) => editorDialog.update(v)}
          options={editorDialog.options}
        />
      )}
      {bulkEditorDialog.inputType && (
        <ScheduleEditorDialog
          targetName={bulkEditorDialog.targetName}
          target={bulkEditorDialog.target}
          type={bulkEditorDialog.inputType}
          value={""}
          onClose={bulkEditorDialog.close}
          onChange={(v) => bulkEditorDialog.update(v)}
          options={bulkEditorDialog.options}
          bulk={true}
        />
      )}
      {historyData && 0 < historyData.list.length ? (
        <ScheduleHistoryDialog
          title={historyData.title}
          target={historyData.target}
          historyList={historyData.list}
          onClose={() => {
            setHistoryData(null);
          }}
        />
      ) : (
        <></>
      )}
    </>
  );
};
